import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import { Textarea } from './ui/textarea';
import { ScrollArea } from './ui/scroll-area';
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogFooter,
    DialogDescription,
} from './ui/dialog';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from './ui/select';
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from './ui/table';
import { 
    Wallet, Plus, Edit, History, RefreshCw, TrendingUp, TrendingDown,
    ArrowUpCircle, ArrowDownCircle, AlertTriangle, CheckCircle, Trash2,
    Clock, User, ShoppingBag, RotateCcw
} from 'lucide-react';

const EXPENSE_CATEGORIES = [
    { id: "legumes", name: "Légumes" },
    { id: "viandes", name: "Viandes & Poissons" },
    { id: "epicerie", name: "Épicerie" },
    { id: "boissons", name: "Boissons" },
    { id: "produits_laitiers", name: "Produits Laitiers" },
    { id: "entretien", name: "Entretien & Nettoyage" },
    { id: "emballages", name: "Emballages" },
    { id: "equipement", name: "Équipement" },
    { id: "autre", name: "Autre" }
];

const CagnotteWidget = ({ onExpenseAdded }) => {
    const { api, user } = useAuth();
    const [cagnotte, setCagnotte] = useState(null);
    const [loading, setLoading] = useState(true);
    const [expenses, setExpenses] = useState([]);
    const [history, setHistory] = useState([]);
    
    // Dialogs
    const [addFundsOpen, setAddFundsOpen] = useState(false);
    const [addExpenseOpen, setAddExpenseOpen] = useState(false);
    const [modifyOpen, setModifyOpen] = useState(false);
    const [historyOpen, setHistoryOpen] = useState(false);
    const [expensesListOpen, setExpensesListOpen] = useState(false);
    const [resetOpen, setResetOpen] = useState(false);
    
    // Forms
    const [addFundsForm, setAddFundsForm] = useState({ amount: '', motif: '', comment: '' });
    const [expenseForm, setExpenseForm] = useState({
        name: '', category: '', quantity: 1, unit: 'unité',
        amount: '', supplier: '', note: ''
    });
    const [modifyForm, setModifyForm] = useState({ new_amount: '', motif: '', comment: '' });
    const [resetForm, setResetForm] = useState({ new_amount: 0, motif: 'Réinitialisation', comment: '' });

    const fetchCagnotte = useCallback(async () => {
        try {
            const res = await api().get('/cagnotte');
            setCagnotte(res.data);
        } catch (error) {
            if (error.response?.status !== 403) {
                toast.error('Erreur lors du chargement de la cagnotte');
            }
            setCagnotte(null);
        } finally {
            setLoading(false);
        }
    }, [api]);

    const fetchExpenses = async () => {
        try {
            const res = await api().get('/cagnotte/expenses?limit=50');
            setExpenses(res.data.expenses || []);
        } catch (error) {
            console.error('Error fetching expenses:', error);
        }
    };

    const fetchHistory = async () => {
        try {
            const res = await api().get('/cagnotte/history?limit=100');
            setHistory(res.data.history || []);
        } catch (error) {
            console.error('Error fetching history:', error);
        }
    };

    useEffect(() => {
        fetchCagnotte();
    }, [fetchCagnotte]);

    const handleAddFunds = async (e) => {
        e.preventDefault();
        if (!addFundsForm.amount || !addFundsForm.motif) {
            toast.error('Veuillez remplir le montant et le motif');
            return;
        }
        try {
            const res = await api().post('/cagnotte/add-funds', {
                amount: parseFloat(addFundsForm.amount),
                motif: addFundsForm.motif,
                comment: addFundsForm.comment || null
            });
            toast.success(res.data.message);
            setAddFundsOpen(false);
            setAddFundsForm({ amount: '', motif: '', comment: '' });
            fetchCagnotte();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Erreur lors de l\'ajout');
        }
    };

    const handleAddExpense = async (e) => {
        e.preventDefault();
        if (!expenseForm.name || !expenseForm.category || !expenseForm.amount) {
            toast.error('Veuillez remplir tous les champs obligatoires');
            return;
        }
        try {
            const res = await api().post('/cagnotte/expense', {
                ...expenseForm,
                quantity: parseFloat(expenseForm.quantity) || 1,
                amount: parseFloat(expenseForm.amount)
            });
            toast.success(res.data.message);
            setAddExpenseOpen(false);
            setExpenseForm({
                name: '', category: '', quantity: 1, unit: 'unité',
                amount: '', supplier: '', note: ''
            });
            fetchCagnotte();
            if (onExpenseAdded) onExpenseAdded();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Erreur lors de l\'enregistrement');
        }
    };

    const handleModify = async (e) => {
        e.preventDefault();
        if (!modifyForm.new_amount || !modifyForm.motif) {
            toast.error('Veuillez remplir tous les champs');
            return;
        }
        try {
            const res = await api().post('/cagnotte/modify', {
                new_amount: parseFloat(modifyForm.new_amount),
                motif: modifyForm.motif,
                comment: modifyForm.comment || null
            });
            toast.success(res.data.message);
            setModifyOpen(false);
            setModifyForm({ new_amount: '', motif: '', comment: '' });
            fetchCagnotte();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Erreur lors de la modification');
        }
    };

    const handleReset = async (e) => {
        e.preventDefault();
        try {
            const res = await api().post('/cagnotte/reset', resetForm);
            toast.success(res.data.message);
            setResetOpen(false);
            setResetForm({ new_amount: 0, motif: 'Réinitialisation', comment: '' });
            fetchCagnotte();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Erreur lors de la réinitialisation');
        }
    };

    const handleDeleteExpense = async (expenseId, expenseName) => {
        if (!window.confirm(`Supprimer la dépense "${expenseName}" et restaurer le montant ?`)) return;
        try {
            const res = await api().delete(`/cagnotte/expense/${expenseId}`);
            toast.success(res.data.message);
            fetchExpenses();
            fetchCagnotte();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Erreur lors de la suppression');
        }
    };

    const formatCurrency = (amount) => {
        return new Intl.NumberFormat('fr-FR').format(amount || 0) + ' FCFA';
    };

    const formatDate = (dateStr) => {
        if (!dateStr) return '-';
        return new Date(dateStr).toLocaleString('fr-FR', {
            day: '2-digit', month: '2-digit', year: 'numeric',
            hour: '2-digit', minute: '2-digit'
        });
    };

    const getMovementIcon = (type) => {
        switch (type) {
            case 'ajout': return <ArrowUpCircle className="w-4 h-4 text-green-500" />;
            case 'deduction': return <ArrowDownCircle className="w-4 h-4 text-red-500" />;
            case 'correction': return <Edit className="w-4 h-4 text-blue-500" />;
            case 'reset': return <RotateCcw className="w-4 h-4 text-purple-500" />;
            default: return <Clock className="w-4 h-4 text-gray-500" />;
        }
    };

    const getMovementLabel = (type) => {
        switch (type) {
            case 'ajout': return 'Ajout de fonds';
            case 'deduction': return 'Dépense';
            case 'correction': return 'Correction';
            case 'reset': return 'Réinitialisation';
            default: return type;
        }
    };

    if (loading) {
        return (
            <Card className="bg-gradient-to-r from-amber-50 to-amber-100 dark:from-amber-900/20 dark:to-amber-800/20 border-amber-200 dark:border-amber-700">
                <CardContent className="pt-6">
                    <div className="flex items-center justify-center py-4">
                        <RefreshCw className="w-6 h-6 animate-spin text-amber-600" />
                    </div>
                </CardContent>
            </Card>
        );
    }

    if (!cagnotte) {
        return null; // User doesn't have permission
    }

    const isLowBalance = cagnotte.current_amount < 50000;
    const isNegative = cagnotte.current_amount < 0;

    return (
        <>
            {/* Main Cagnotte Card */}
            <Card className={`border-2 ${
                isNegative ? 'bg-red-50 dark:bg-red-900/20 border-red-300 dark:border-red-700' :
                isLowBalance ? 'bg-amber-50 dark:bg-amber-900/20 border-amber-300 dark:border-amber-700' :
                'bg-gradient-to-r from-green-50 to-emerald-50 dark:from-green-900/20 dark:to-emerald-900/20 border-green-300 dark:border-green-700'
            }`} data-testid="cagnotte-widget">
                <CardContent className="pt-6">
                    <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
                        {/* Left: Cagnotte Info */}
                        <div className="flex items-center gap-4">
                            <div className={`p-3 rounded-xl ${
                                isNegative ? 'bg-red-500' :
                                isLowBalance ? 'bg-amber-500' : 'bg-green-500'
                            }`}>
                                <Wallet className="w-8 h-8 text-white" />
                            </div>
                            <div>
                                <p className="text-sm font-medium text-muted-foreground flex items-center gap-2">
                                    Cagnotte Disponible
                                    {isLowBalance && !isNegative && (
                                        <Badge variant="outline" className="text-amber-600 border-amber-400">
                                            <AlertTriangle className="w-3 h-3 mr-1" />
                                            Solde bas
                                        </Badge>
                                    )}
                                    {isNegative && (
                                        <Badge variant="destructive">
                                            <AlertTriangle className="w-3 h-3 mr-1" />
                                            Solde négatif
                                        </Badge>
                                    )}
                                </p>
                                <p className={`text-3xl font-bold ${
                                    isNegative ? 'text-red-600' :
                                    isLowBalance ? 'text-amber-600' : 'text-green-600'
                                }`}>
                                    {formatCurrency(cagnotte.current_amount)}
                                </p>
                                <p className="text-xs text-muted-foreground mt-1">
                                    Dernière mise à jour: {formatDate(cagnotte.updated_at)}
                                    {cagnotte.last_modified_by_name && (
                                        <span> par {cagnotte.last_modified_by_name}</span>
                                    )}
                                </p>
                            </div>
                        </div>

                        {/* Right: Action Buttons */}
                        <div className="flex flex-wrap gap-2">
                            {/* Everyone who can see can add expense */}
                            {cagnotte.can_add_expense && (
                                <Button
                                    onClick={() => setAddExpenseOpen(true)}
                                    className="bg-red-600 hover:bg-red-700"
                                    data-testid="add-expense-btn"
                                >
                                    <ShoppingBag className="w-4 h-4 mr-2" />
                                    Ajouter Dépense
                                </Button>
                            )}
                            
                            {/* Only managers can add funds */}
                            {cagnotte.can_manage && (
                                <>
                                    <Button
                                        onClick={() => setAddFundsOpen(true)}
                                        className="bg-green-600 hover:bg-green-700"
                                        data-testid="add-funds-btn"
                                    >
                                        <Plus className="w-4 h-4 mr-2" />
                                        Ajouter Fonds
                                    </Button>
                                    <Button
                                        variant="outline"
                                        onClick={() => {
                                            setModifyForm({ 
                                                new_amount: cagnotte.current_amount.toString(), 
                                                motif: '', 
                                                comment: '' 
                                            });
                                            setModifyOpen(true);
                                        }}
                                        data-testid="modify-cagnotte-btn"
                                    >
                                        <Edit className="w-4 h-4 mr-2" />
                                        Modifier
                                    </Button>
                                </>
                            )}
                            
                            {/* View expenses and history */}
                            <Button
                                variant="outline"
                                onClick={() => {
                                    fetchExpenses();
                                    setExpensesListOpen(true);
                                }}
                                data-testid="view-expenses-btn"
                            >
                                <TrendingDown className="w-4 h-4 mr-2" />
                                Dépenses
                            </Button>
                            
                            <Button
                                variant="outline"
                                onClick={() => {
                                    fetchHistory();
                                    setHistoryOpen(true);
                                }}
                                data-testid="view-history-btn"
                            >
                                <History className="w-4 h-4 mr-2" />
                                Historique
                            </Button>

                            {/* Only Admin can reset */}
                            {cagnotte.can_reset && (
                                <Button
                                    variant="ghost"
                                    onClick={() => setResetOpen(true)}
                                    className="text-purple-600 hover:text-purple-700 hover:bg-purple-50"
                                    data-testid="reset-cagnotte-btn"
                                >
                                    <RotateCcw className="w-4 h-4 mr-2" />
                                    Réinitialiser
                                </Button>
                            )}
                        </div>
                    </div>
                </CardContent>
            </Card>

            {/* Add Funds Dialog */}
            <Dialog open={addFundsOpen} onOpenChange={setAddFundsOpen}>
                <DialogContent className="max-w-md">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2">
                            <TrendingUp className="w-5 h-5 text-green-500" />
                            Ajouter des Fonds
                        </DialogTitle>
                        <DialogDescription>
                            Le montant sera ajouté à la cagnotte actuelle
                        </DialogDescription>
                    </DialogHeader>
                    <form onSubmit={handleAddFunds} className="space-y-4">
                        <div>
                            <Label htmlFor="add-amount">Montant (FCFA) *</Label>
                            <Input
                                id="add-amount"
                                type="number"
                                min="0"
                                step="100"
                                value={addFundsForm.amount}
                                onChange={(e) => setAddFundsForm({...addFundsForm, amount: e.target.value})}
                                placeholder="Ex: 100000"
                                required
                            />
                        </div>
                        <div>
                            <Label htmlFor="add-motif">Motif *</Label>
                            <Input
                                id="add-motif"
                                value={addFundsForm.motif}
                                onChange={(e) => setAddFundsForm({...addFundsForm, motif: e.target.value})}
                                placeholder="Ex: Approvisionnement mensuel"
                                required
                            />
                        </div>
                        <div>
                            <Label htmlFor="add-comment">Commentaire</Label>
                            <Textarea
                                id="add-comment"
                                value={addFundsForm.comment}
                                onChange={(e) => setAddFundsForm({...addFundsForm, comment: e.target.value})}
                                placeholder="Détails supplémentaires..."
                                rows={2}
                            />
                        </div>
                        <DialogFooter>
                            <Button type="button" variant="outline" onClick={() => setAddFundsOpen(false)}>
                                Annuler
                            </Button>
                            <Button type="submit" className="bg-green-600 hover:bg-green-700">
                                <Plus className="w-4 h-4 mr-2" />
                                Ajouter
                            </Button>
                        </DialogFooter>
                    </form>
                </DialogContent>
            </Dialog>

            {/* Add Expense Dialog */}
            <Dialog open={addExpenseOpen} onOpenChange={setAddExpenseOpen}>
                <DialogContent className="max-w-lg">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2">
                            <ShoppingBag className="w-5 h-5 text-red-500" />
                            Ajouter une Dépense
                        </DialogTitle>
                        <DialogDescription>
                            Le montant sera déduit automatiquement de la cagnotte
                            <span className="block mt-1 font-medium text-foreground">
                                Solde actuel: {formatCurrency(cagnotte?.current_amount)}
                            </span>
                        </DialogDescription>
                    </DialogHeader>
                    <form onSubmit={handleAddExpense} className="space-y-4">
                        <div className="grid grid-cols-2 gap-4">
                            <div className="col-span-2">
                                <Label htmlFor="expense-name">Nom de l'achat *</Label>
                                <Input
                                    id="expense-name"
                                    value={expenseForm.name}
                                    onChange={(e) => setExpenseForm({...expenseForm, name: e.target.value})}
                                    placeholder="Ex: Tomates, Poulet, etc."
                                    required
                                />
                            </div>
                            <div>
                                <Label htmlFor="expense-category">Catégorie *</Label>
                                <Select 
                                    value={expenseForm.category} 
                                    onValueChange={(val) => setExpenseForm({...expenseForm, category: val})}
                                >
                                    <SelectTrigger>
                                        <SelectValue placeholder="Sélectionner..." />
                                    </SelectTrigger>
                                    <SelectContent>
                                        {EXPENSE_CATEGORIES.map(cat => (
                                            <SelectItem key={cat.id} value={cat.id}>{cat.name}</SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>
                            <div>
                                <Label htmlFor="expense-amount">Montant (FCFA) *</Label>
                                <Input
                                    id="expense-amount"
                                    type="number"
                                    min="0"
                                    step="100"
                                    value={expenseForm.amount}
                                    onChange={(e) => setExpenseForm({...expenseForm, amount: e.target.value})}
                                    placeholder="Ex: 5000"
                                    required
                                />
                            </div>
                            <div>
                                <Label htmlFor="expense-quantity">Quantité</Label>
                                <Input
                                    id="expense-quantity"
                                    type="number"
                                    min="0.1"
                                    step="0.1"
                                    value={expenseForm.quantity}
                                    onChange={(e) => setExpenseForm({...expenseForm, quantity: e.target.value})}
                                />
                            </div>
                            <div>
                                <Label htmlFor="expense-unit">Unité</Label>
                                <Select 
                                    value={expenseForm.unit} 
                                    onValueChange={(val) => setExpenseForm({...expenseForm, unit: val})}
                                >
                                    <SelectTrigger>
                                        <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="unité">Unité</SelectItem>
                                        <SelectItem value="kg">Kg</SelectItem>
                                        <SelectItem value="g">Grammes</SelectItem>
                                        <SelectItem value="L">Litres</SelectItem>
                                        <SelectItem value="paquet">Paquet</SelectItem>
                                        <SelectItem value="carton">Carton</SelectItem>
                                        <SelectItem value="sac">Sac</SelectItem>
                                    </SelectContent>
                                </Select>
                            </div>
                            <div className="col-span-2">
                                <Label htmlFor="expense-supplier">Fournisseur</Label>
                                <Input
                                    id="expense-supplier"
                                    value={expenseForm.supplier}
                                    onChange={(e) => setExpenseForm({...expenseForm, supplier: e.target.value})}
                                    placeholder="Nom du fournisseur"
                                />
                            </div>
                            <div className="col-span-2">
                                <Label htmlFor="expense-note">Note</Label>
                                <Textarea
                                    id="expense-note"
                                    value={expenseForm.note}
                                    onChange={(e) => setExpenseForm({...expenseForm, note: e.target.value})}
                                    placeholder="Remarques..."
                                    rows={2}
                                />
                            </div>
                        </div>
                        
                        {/* Warning if expense exceeds balance */}
                        {parseFloat(expenseForm.amount) > (cagnotte?.current_amount || 0) && (
                            <div className="flex items-center gap-2 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-700 rounded-lg">
                                <AlertTriangle className="w-5 h-5 text-red-500" />
                                <p className="text-sm text-red-600 dark:text-red-400">
                                    Attention: Ce montant dépasse le solde disponible !
                                </p>
                            </div>
                        )}
                        
                        <DialogFooter>
                            <Button type="button" variant="outline" onClick={() => setAddExpenseOpen(false)}>
                                Annuler
                            </Button>
                            <Button type="submit" className="bg-red-600 hover:bg-red-700">
                                <ShoppingBag className="w-4 h-4 mr-2" />
                                Enregistrer Dépense
                            </Button>
                        </DialogFooter>
                    </form>
                </DialogContent>
            </Dialog>

            {/* Modify Cagnotte Dialog */}
            <Dialog open={modifyOpen} onOpenChange={setModifyOpen}>
                <DialogContent className="max-w-md">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2">
                            <Edit className="w-5 h-5 text-blue-500" />
                            Modifier la Cagnotte
                        </DialogTitle>
                        <DialogDescription>
                            Correction manuelle du solde de la cagnotte
                        </DialogDescription>
                    </DialogHeader>
                    <form onSubmit={handleModify} className="space-y-4">
                        <div>
                            <Label htmlFor="modify-amount">Nouveau Montant (FCFA) *</Label>
                            <Input
                                id="modify-amount"
                                type="number"
                                min="0"
                                step="100"
                                value={modifyForm.new_amount}
                                onChange={(e) => setModifyForm({...modifyForm, new_amount: e.target.value})}
                                required
                            />
                        </div>
                        <div>
                            <Label htmlFor="modify-motif">Motif de la correction *</Label>
                            <Input
                                id="modify-motif"
                                value={modifyForm.motif}
                                onChange={(e) => setModifyForm({...modifyForm, motif: e.target.value})}
                                placeholder="Ex: Correction suite à inventaire"
                                required
                            />
                        </div>
                        <div>
                            <Label htmlFor="modify-comment">Commentaire</Label>
                            <Textarea
                                id="modify-comment"
                                value={modifyForm.comment}
                                onChange={(e) => setModifyForm({...modifyForm, comment: e.target.value})}
                                rows={2}
                            />
                        </div>
                        <DialogFooter>
                            <Button type="button" variant="outline" onClick={() => setModifyOpen(false)}>
                                Annuler
                            </Button>
                            <Button type="submit" className="bg-blue-600 hover:bg-blue-700">
                                <CheckCircle className="w-4 h-4 mr-2" />
                                Valider
                            </Button>
                        </DialogFooter>
                    </form>
                </DialogContent>
            </Dialog>

            {/* Reset Cagnotte Dialog (Admin only) */}
            <Dialog open={resetOpen} onOpenChange={setResetOpen}>
                <DialogContent className="max-w-md">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2 text-purple-600">
                            <RotateCcw className="w-5 h-5" />
                            Réinitialiser la Cagnotte
                        </DialogTitle>
                        <DialogDescription className="text-red-500">
                            ⚠️ Action réservée à l'administrateur
                        </DialogDescription>
                    </DialogHeader>
                    <form onSubmit={handleReset} className="space-y-4">
                        <div>
                            <Label htmlFor="reset-amount">Nouveau Montant Initial (FCFA)</Label>
                            <Input
                                id="reset-amount"
                                type="number"
                                min="0"
                                step="100"
                                value={resetForm.new_amount}
                                onChange={(e) => setResetForm({...resetForm, new_amount: parseFloat(e.target.value) || 0})}
                            />
                        </div>
                        <div>
                            <Label htmlFor="reset-motif">Motif</Label>
                            <Input
                                id="reset-motif"
                                value={resetForm.motif}
                                onChange={(e) => setResetForm({...resetForm, motif: e.target.value})}
                            />
                        </div>
                        <div>
                            <Label htmlFor="reset-comment">Commentaire</Label>
                            <Textarea
                                id="reset-comment"
                                value={resetForm.comment}
                                onChange={(e) => setResetForm({...resetForm, comment: e.target.value})}
                                rows={2}
                            />
                        </div>
                        <DialogFooter>
                            <Button type="button" variant="outline" onClick={() => setResetOpen(false)}>
                                Annuler
                            </Button>
                            <Button type="submit" className="bg-purple-600 hover:bg-purple-700">
                                <RotateCcw className="w-4 h-4 mr-2" />
                                Réinitialiser
                            </Button>
                        </DialogFooter>
                    </form>
                </DialogContent>
            </Dialog>

            {/* Expenses List Dialog */}
            <Dialog open={expensesListOpen} onOpenChange={setExpensesListOpen}>
                <DialogContent className="max-w-4xl max-h-[80vh]">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2">
                            <TrendingDown className="w-5 h-5 text-red-500" />
                            Liste des Dépenses
                        </DialogTitle>
                        <DialogDescription>
                            Toutes les dépenses effectuées sur la cagnotte
                        </DialogDescription>
                    </DialogHeader>
                    <ScrollArea className="h-[60vh]">
                        {expenses.length === 0 ? (
                            <div className="text-center py-8 text-muted-foreground">
                                Aucune dépense enregistrée
                            </div>
                        ) : (
                            <Table>
                                <TableHeader>
                                    <TableRow>
                                        <TableHead>Date</TableHead>
                                        <TableHead>Achat</TableHead>
                                        <TableHead>Catégorie</TableHead>
                                        <TableHead>Qté</TableHead>
                                        <TableHead>Montant</TableHead>
                                        <TableHead>Par</TableHead>
                                        <TableHead>Solde après</TableHead>
                                        {cagnotte?.can_manage && <TableHead>Actions</TableHead>}
                                    </TableRow>
                                </TableHeader>
                                <TableBody>
                                    {expenses.map((expense) => (
                                        <TableRow key={expense.id}>
                                            <TableCell className="text-xs">
                                                {formatDate(expense.created_at)}
                                            </TableCell>
                                            <TableCell className="font-medium">{expense.name}</TableCell>
                                            <TableCell>
                                                <Badge variant="outline">
                                                    {EXPENSE_CATEGORIES.find(c => c.id === expense.category)?.name || expense.category}
                                                </Badge>
                                            </TableCell>
                                            <TableCell>{expense.quantity} {expense.unit}</TableCell>
                                            <TableCell className="font-medium text-red-600">
                                                -{formatCurrency(expense.amount)}
                                            </TableCell>
                                            <TableCell className="text-xs">
                                                {expense.user_name}
                                                <br />
                                                <span className="text-muted-foreground">{expense.user_role}</span>
                                            </TableCell>
                                            <TableCell>{formatCurrency(expense.balance_after)}</TableCell>
                                            {cagnotte?.can_manage && (
                                                <TableCell>
                                                    <Button
                                                        variant="ghost"
                                                        size="sm"
                                                        onClick={() => handleDeleteExpense(expense.id, expense.name)}
                                                        className="text-red-500 hover:text-red-700 hover:bg-red-50"
                                                    >
                                                        <Trash2 className="w-4 h-4" />
                                                    </Button>
                                                </TableCell>
                                            )}
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        )}
                    </ScrollArea>
                </DialogContent>
            </Dialog>

            {/* History Dialog */}
            <Dialog open={historyOpen} onOpenChange={setHistoryOpen}>
                <DialogContent className="max-w-4xl max-h-[80vh]">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2">
                            <History className="w-5 h-5 text-blue-500" />
                            Historique des Mouvements
                        </DialogTitle>
                        <DialogDescription>
                            Tous les mouvements de la cagnotte (ajouts, déductions, corrections)
                        </DialogDescription>
                    </DialogHeader>
                    <ScrollArea className="h-[60vh]">
                        {history.length === 0 ? (
                            <div className="text-center py-8 text-muted-foreground">
                                Aucun mouvement enregistré
                            </div>
                        ) : (
                            <Table>
                                <TableHeader>
                                    <TableRow>
                                        <TableHead>Date</TableHead>
                                        <TableHead>Type</TableHead>
                                        <TableHead>Montant</TableHead>
                                        <TableHead>Ancien Solde</TableHead>
                                        <TableHead>Nouveau Solde</TableHead>
                                        <TableHead>Par</TableHead>
                                        <TableHead>Motif</TableHead>
                                    </TableRow>
                                </TableHeader>
                                <TableBody>
                                    {history.map((entry) => (
                                        <TableRow key={entry.id}>
                                            <TableCell className="text-xs">
                                                {formatDate(entry.timestamp)}
                                            </TableCell>
                                            <TableCell>
                                                <div className="flex items-center gap-2">
                                                    {getMovementIcon(entry.movement_type)}
                                                    <span>{getMovementLabel(entry.movement_type)}</span>
                                                </div>
                                            </TableCell>
                                            <TableCell className={`font-medium ${
                                                entry.movement_type === 'ajout' ? 'text-green-600' :
                                                entry.movement_type === 'deduction' ? 'text-red-600' :
                                                'text-blue-600'
                                            }`}>
                                                {entry.movement_type === 'ajout' ? '+' : 
                                                 entry.movement_type === 'deduction' ? '-' : ''}
                                                {formatCurrency(Math.abs(entry.amount))}
                                            </TableCell>
                                            <TableCell>{formatCurrency(entry.old_balance)}</TableCell>
                                            <TableCell className="font-medium">
                                                {formatCurrency(entry.new_balance)}
                                            </TableCell>
                                            <TableCell className="text-xs">
                                                {entry.user_name}
                                                <br />
                                                <span className="text-muted-foreground">{entry.user_role}</span>
                                            </TableCell>
                                            <TableCell className="max-w-[200px] truncate" title={entry.motif}>
                                                {entry.motif}
                                                {entry.comment && (
                                                    <p className="text-xs text-muted-foreground truncate">
                                                        {entry.comment}
                                                    </p>
                                                )}
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        )}
                    </ScrollArea>
                </DialogContent>
            </Dialog>
        </>
    );
};

export default CagnotteWidget;
